import { SavedCoachingReservation } from 'coaching/containers/GetAvailableReservationsContainer';
import CurrentUser from 'auth/CurrentUser';
// @ts-ignore

import {
  ExperimentInfo,
  getStoredExperimentInfo,
  saveExperimentInfo,
} from 'coaching/experiment/FunnelExperimentDataStore';
import { funnelExperimentDisabledGlobally } from 'coaching/experiment/ExperimentEnablement';
import { toNumber } from 'lodash';
// @ts-ignore
import Config from 'Config';
import MetricTracking from 'lib/MetricTracking';

const FUNNEL_EXPERIMENT_TEST_ID_STRING = "0";
const FUNNEL_EXPERIMENT_TEST_ID = toNumber(FUNNEL_EXPERIMENT_TEST_ID_STRING);

export type PurchaseData = {
  student: {
    studentName: string;
  };
  selectedReservation: SavedCoachingReservation;
  userTimezone: string;
  selectedSubject: string;
  selectedGradeLevel: string;
  source: string;
};

/** Check if the sign in funnel experiment should be enabled and save this in storage.*/
export function enableExperimentIfAppropriate() {
  //we don't want to interact with storage if the experiment is disabled globally
  if (funnelExperimentDisabledGlobally()) {
    return;
  }

  const enabled = !CurrentUser.isSignedIn() && experimentVariantIsEnabled();
  setExperimentEnabled(enabled);
}

export function setExperimentEnabled(shouldEnable: boolean) {
  if (funnelExperimentDisabledGlobally()) {
    return;
  }
  //Whenever we enable/disable, overwrite all existing information to avoid potential issues
  //with data left over from previous attempts
  const experimentInfo = initialExperimentInfo(shouldEnable);
  saveExperimentInfo(experimentInfo);
}

export function finishExperiment() {
  if (funnelExperimentDisabledGlobally()) {
    return;
  }

  saveExperimentInfo(null);
}

let shouldLoad = false;
export function setShouldLoadFunnelTest(_shouldLoad: boolean) {
  shouldLoad = _shouldLoad;
}

export function shouldLoadFunnelTest() {
  return shouldLoad;
}

export function experimentEnabled() {
  if (funnelExperimentDisabledGlobally()) {
    return false;
  }
  const enabled = getExperimentInfo().experimentEnabled;
  return !!enabled;
}

function getExperimentInfo(): ExperimentInfo {
  const storedInfo = getStoredExperimentInfo();
  return storedInfo ?? initialExperimentInfo(false);
}

function initialExperimentInfo(shouldEnable: boolean) {
  return {
    experimentEnabled: shouldEnable,
  };
}

export function getPurchaseData(): PurchaseData | undefined {
  return getExperimentInfo().purchaseData;
}

export function savePurchaseData(purchaseData: PurchaseData) {
  if (funnelExperimentDisabledGlobally()) {
    return;
  }
  saveExperimentInfo({
    ...getExperimentInfo(),
    purchaseData,
  });
}

const AB_TASTY_WAIT_TIMEOUT = 10000;
/** Wait until AB tasty load. If it doesn't load in a certain amount of time complete anyways **/
export async function waitForABTasty() {
  const startTime = Date.now();
  const abTastyCampaignExecuted = await waitUntilCampaignExecuted(startTime);
  const endTime = Date.now();
  if (abTastyCampaignExecuted) {
    const loadtime = endTime - startTime;
    MetricTracking.timingMilliseconds('coaching.signinfunnel.abtasty.loadtime', loadtime);
  } else {
    MetricTracking.count('coaching.signinfunnel.abtasty.timeout', 1);
  }
  MetricTracking.timingMilliseconds(
    'coaching.signinfunnel.loadtime',
    endTime - window.performance.timing.navigationStart
  );
}

async function waitUntilCampaignExecuted(startTime: number) {
  let campaignExecuted = false;
  function campaignExecutedListener(evt: Event) {
    if ((evt as any)?.detail?.campaignId === FUNNEL_EXPERIMENT_TEST_ID) {
      campaignExecuted = true;
    }
  }
  window.addEventListener('abtasty_executedCampaign', campaignExecutedListener);

  try {
    while (Date.now() - startTime < AB_TASTY_WAIT_TIMEOUT) {
      if (campaignExecuted || testLoadedOnPage()) {
        return true;
      }
      await new Promise<void>(resolve => {
        setTimeout(() => resolve(), 10);
      });
    }
    return false;
  } catch (e) {
    console.error(`Error loading ABTasty ${e}`);
    return false;
  } finally {
    window.removeEventListener('abtasty_executedCampaign', campaignExecutedListener);
  }
}

export function testLoadedOnPage() {
  const tests = (window as any)?.ABTasty?.getTestsOnPage?.();
  const experimentVariation = tests?.[FUNNEL_EXPERIMENT_TEST_ID_STRING]?.variationID;
  return typeof experimentVariation === 'number';
}

export function experimentVariantIsEnabled() {
  const tests = (window as any)?.ABTasty?.getTestsOnPage?.();
  const experimentVariation = tests?.[FUNNEL_EXPERIMENT_TEST_ID_STRING]?.variationID;
  const isExperimentVariant = typeof experimentVariation === 'number' && experimentVariation !== 0;
  return isExperimentVariant;
}
